import { PartyType } from '../../../js/interfaces/CoverpathTypes';

export enum Tense {
  present = 'present',
  past = 'past',
}

export enum PointOfView {
  'First',
  'Second',
  'Third',
  'FirstPlural',
  'SecondPlural',
  'ThirdPlural',
}

export enum PredicateType {
  normal = 'normal',
  possessive = 'possessive',
  inverted = 'inverted',
  object = 'object',
  subject = 'subject',
}

export enum Conjunction {
  and = 'and',
  or = 'or',
  andor = 'and/or',
  any = 'any',
  we = 'we',
  both = 'both',
}

export type NounPhrase = {
  headNoun: string;
  preModifier: string;
};

type Conjugation = {
  [PointOfView.First]: string;
  [PointOfView.Second]: string;
  [PointOfView.Third]: string;
  [PointOfView.FirstPlural]?: string;
  [PointOfView.SecondPlural]?: string;
  [PointOfView.ThirdPlural]?: string;
};

export type Verb = {
  [Tense.present]: Conjugation;
  [Tense.past]?: Conjugation;
};

export abstract class Noun {
  // Cases
  abstract Subject: string;
  abstract Object: string;
  abstract Possessive: string;
  abstract PossessiveNoun: string;
  abstract Reflexive: string;

  isPlural: boolean = false;
}

export class ProperNoun extends Noun {
  private name: string;

  constructor(name: string, secondName?: string, conjunction?: Conjunction) {
    super();
    this.name = name;
    if (secondName && conjunction) {
      this.name += ` ${conjunction} ${secondName}`;
      this.isPlural = true;
    }
  }

  get Subject(): string {
    return this.name;
  }
  get Object(): string {
    return this.Subject;
  }
  get Possessive(): string {
    return `${this.name}'s`;
  }
  get PossessiveNoun(): string {
    return this.Possessive;
  }
  get Reflexive(): string {
    return this.Subject;
  }
}

export class Pronoun extends Noun {
  // Cases
  Subject: string;
  Object: string;
  Possessive: string;
  PossessiveNoun: string;
  Reflexive: string;
}

export type Predicate = {
  noun: PartyType;
  useFullName?: boolean;
  verb?: Verb;
  type: PredicateType;
  capitalize?: boolean;
  tense?: Tense;
  secondTense?: Tense;
  conjunction?: Conjunction;
  nounPhrase?: NounPhrase;
};

export const GrammarTable = {
  Verbs: {
    ToBe: <Verb>{
      [Tense.present]: {
        [PointOfView.First]: 'am',
        [PointOfView.Second]: 'are',
        [PointOfView.Third]: 'is',
        [PointOfView.FirstPlural]: 'are',
        [PointOfView.ThirdPlural]: 'are',
      },
      [Tense.past]: {
        [PointOfView.First]: 'was',
        [PointOfView.Second]: 'were',
        [PointOfView.Third]: 'was',
        [PointOfView.ThirdPlural]: 'were',
      },
    },
    ToDo: <Verb>{
      [Tense.present]: {
        [PointOfView.First]: 'do',
        [PointOfView.Second]: 'do',
        [PointOfView.Third]: 'does',
        [PointOfView.ThirdPlural]: 'do',
      },
      [Tense.past]: {
        [PointOfView.First]: 'did',
        [PointOfView.Second]: 'did',
        [PointOfView.Third]: 'did',
        [PointOfView.ThirdPlural]: 'did',
      },
    },
    ToBecome: <Verb>{
      [Tense.present]: {
        [PointOfView.First]: 'become',
        [PointOfView.Second]: 'become',
        [PointOfView.Third]: 'becomes',
        [PointOfView.ThirdPlural]: 'become',
      },
      [Tense.past]: {
        [PointOfView.First]: 'became',
        [PointOfView.Second]: 'became',
        [PointOfView.Third]: 'became',
        [PointOfView.ThirdPlural]: 'became',
      },
    },
    ToHave: <Verb>{
      [Tense.present]: {
        [PointOfView.First]: 'have',
        [PointOfView.Second]: 'have',
        [PointOfView.Third]: 'has',
        [PointOfView.ThirdPlural]: 'have',
      },
      [Tense.past]: {
        [PointOfView.First]: 'had',
        [PointOfView.Second]: 'had',
        [PointOfView.Third]: 'had',
        [PointOfView.ThirdPlural]: 'had',
      },
    },
    ToParticipate: <Verb>{
      [Tense.present]: {
        [PointOfView.First]: 'participate',
        [PointOfView.Second]: 'participate',
        [PointOfView.Third]: 'participates',
      },
      [Tense.past]: {
        [PointOfView.First]: 'participated',
        [PointOfView.Second]: 'participated',
        [PointOfView.Third]: 'participated',
      },
    },
    ToDie: <Verb>{
      [Tense.present]: {
        [PointOfView.First]: 'die',
        [PointOfView.Second]: 'die',
        [PointOfView.Third]: 'dies',
      },
      [Tense.past]: {
        [PointOfView.First]: 'died',
        [PointOfView.Second]: 'died',
        [PointOfView.Third]: 'died',
      },
    },
    ToUse: <Verb>{
      [Tense.present]: {
        [PointOfView.First]: 'use',
        [PointOfView.Second]: 'use',
        [PointOfView.Third]: 'uses',
      },
      [Tense.past]: {
        [PointOfView.First]: 'used',
        [PointOfView.Second]: 'used',
        [PointOfView.Third]: 'used',
      },
    },
    ToOwn: <Verb>{
      [Tense.present]: {
        [PointOfView.First]: 'own',
        [PointOfView.Second]: 'own',
        [PointOfView.Third]: 'owns',
      },
      [Tense.past]: {
        [PointOfView.First]: 'owned',
        [PointOfView.Second]: 'owned',
        [PointOfView.Third]: 'owned',
      },
    },
  },

  Pronouns: {
    I: <Pronoun>{
      Subject: 'I',
      Object: 'me',
      Possessive: 'my',
      PossessiveNoun: 'mine',
      Reflexive: 'myself',
    },
    You: <Pronoun>{
      Subject: 'you',
      Object: 'you',
      Possessive: 'your',
      PossessiveNoun: 'yours',
      Reflexive: 'yourself',
    },
    We: <Pronoun>{
      Subject: 'we',
      Object: 'us',
      Possessive: 'our',
      PossessiveNoun: 'ours',
      Reflexive: 'ourself',
    },
    They: <Pronoun>{
      Subject: 'they',
      Object: 'them',
      Possessive: 'their',
      PossessiveNoun: 'theirs',
      Reflexive: 'themselves',
    },
  },

  Nouns: {
    Person: {
      Singular: 'person',
      Plural: 'people',
    },
  },
};
